import React, { useMemo, useState } from 'react';
import { FileText, Loader2, Search, Sparkles } from 'lucide-react';
import { useDebouncedValue } from '../../../shared/hooks/useDebouncedValue.ts';
import { getApiErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import { useLibraryFiles, useLibraryLimits, useLibrarySuggestions } from '../../../hooks/evidence-library/useEvidenceLibrary.ts';
import type { EvidenceLibraryFile, EvidenceRole, FileSuggestion } from '../../../request/evidence-library/evidenceLibraryRequests.ts';
import { formatBytes } from '../folderFiles.ts';
import { ROLE_LABELS, ROLE_PILL, STATUS_LABELS, STATUS_PILL, roleAllowedFor } from '../labels.ts';
import { LibraryDialogShell, fieldClass, primaryBtn, secondaryBtn } from './LibraryDialogShell.tsx';
import { PickerFolderTree } from './PickerFolderTree.tsx';
import { buildPathTree } from '../pathTree.ts';

export type LibrarySelection = {
  file: EvidenceLibraryFile;
  role: EvidenceRole;
  /** Sample ids (record refs) for evidence. */
  samples: string[];
};

type Choice = { role: EvidenceRole; samples: string[] };

/** A file on this control picked for the test, with the samples suggested for it (from its name or folder). */
export type ControlFilePick = { evidenceId: number; samples: string[] };

/** A file already linked to this control, offered for adding to the current test. */
export type ControlFileOption = {
  /** control_evidence id */
  evidenceId: number;
  uploadId: number;
  filename: string;
  role: EvidenceRole;
  /** Folder path it was uploaded with, if any. */
  path?: string | null;
  /** e.g. "v2 · uploaded 3 Oct" when the control has several files with this name. */
  note?: string;
};

// Large enough that a typical PBC folder shows as one tree; bigger libraries page.
const PAGE_SIZE = 500;

function FileLine({
  file,
  suggestion,
  selected,
  choice,
  single,
  disabledReason,
  roles,
  samples,
  populationExts,
  onToggle,
  onChoice,
  depth = 0,
  showPath = true,
}: {
  depth?: number;
  showPath?: boolean;
  file: EvidenceLibraryFile;
  suggestion?: FileSuggestion;
  selected: boolean;
  choice?: Choice;
  single: boolean;
  disabledReason?: string;
  roles: EvidenceRole[];
  samples: { id: string; label: string }[];
  populationExts: string[];
  onToggle: () => void;
  onChoice: (c: Choice) => void;
}) {
  const roleOptions = roles.filter((r) => roleAllowedFor(r, file.filename, populationExts));
  const inputId = `lib-file-${file.id}`;
  return (
    <li className={`px-3 py-2 ${selected ? 'bg-blue-50/60' : ''}`} style={depth ? { paddingLeft: 12 + depth * 18 + 20 } : undefined}>
      <div className="flex items-start gap-2">
        <input
          id={inputId}
          type={single ? 'radio' : 'checkbox'}
          name="library-pick"
          className="mt-1"
          checked={selected}
          disabled={Boolean(disabledReason) || roleOptions.length === 0}
          onChange={onToggle}
        />
        <label htmlFor={inputId} className="min-w-0 flex-1 cursor-pointer">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span className="truncate text-sm font-medium text-gray-800">{file.filename}</span>
            <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${STATUS_PILL[file.status]}`}>{STATUS_LABELS[file.status]}</span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
            {showPath && file.relative_path && file.relative_path !== file.filename ? <span className="truncate" title={file.relative_path}>{file.relative_path}</span> : null}
            <span>{formatBytes(file.file_size)}</span>
            {disabledReason ? <span className="text-gray-400">{disabledReason}</span> : null}
            {!disabledReason && roleOptions.length === 0 ? <span className="text-amber-600">Not a population file type</span> : null}
          </div>
          {suggestion?.reasons?.length ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {suggestion.reasons.map((r) => (
                <span key={r} className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700">{r}</span>
              ))}
            </div>
          ) : null}
        </label>
      </div>
      {selected && choice ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 pl-6">
          {roleOptions.length > 1 ? (
            <select
              aria-label={`Role for ${file.filename}`}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs"
              value={choice.role}
              onChange={(e) => onChoice({ ...choice, role: e.target.value as EvidenceRole })}
            >
              {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          ) : (
            <span className={`rounded-full border px-2 py-0.5 text-[10px] ${ROLE_PILL[choice.role]}`}>{ROLE_LABELS[choice.role]}</span>
          )}
          {choice.role === 'evidence' && samples.length ? (
            <div className="flex flex-wrap gap-1">
              {samples.map((s) => {
                const on = choice.samples.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onChoice({ ...choice, samples: on ? choice.samples.filter((x) => x !== s.id) : [...choice.samples, s.id] })}
                    className={`rounded-full border px-2 py-0.5 text-[10px] ${on ? 'border-green-300 bg-green-100 text-green-800' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'}`}
                    title={s.id}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * "Select from library": lists library files with the ones that match this control (and the run's samples) on
 * top, lets the user pick files and set each one's role (and samples), and hands the picks back. The caller links
 * them, since what follows a pick differs by screen (evidence mapping, population parse).
 */
export function LibraryPicker({
  title = 'Select from evidence library',
  soxControlId,
  testRunId,
  auditYearId,
  roles,
  samples = [],
  single = false,
  linkedUploadIds,
  inTestUploadIds,
  controlFiles,
  confirmLabel = 'Add selected',
  onConfirm,
  onClose,
}: {
  title?: string;
  soxControlId: string;
  testRunId?: string;
  auditYearId?: string | null;
  /** Roles the user may choose here; the first is the default when there is no suggestion. */
  roles: EvidenceRole[];
  samples?: { id: string; label: string }[];
  single?: boolean;
  /** Uploads already linked to this control (shown, not selectable). */
  linkedUploadIds?: Set<number>;
  /** Uploads already in the current test (shown as such, not selectable). */
  inTestUploadIds?: Set<number>;
  /**
   * Files on this control that are not in the current test yet, listed first. Picking one hands back its evidence
   * id (second argument of onConfirm): it needs no linking, only adding to the test.
   */
  controlFiles?: ControlFileOption[];
  confirmLabel?: string;
  onConfirm: (picks: LibrarySelection[], controlPicks: ControlFilePick[]) => Promise<void> | void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const q = useDebouncedValue(query);
  const [page, setPage] = useState(1);
  const [chosen, setChosen] = useState<Map<number, { file: EvidenceLibraryFile; choice: Choice }>>(new Map());
  const [chosenControl, setChosenControl] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const { data: limits } = useLibraryLimits();
  const populationExts = limits?.population_extensions ?? [];
  // Files of other years are not offered: a locked year's files cannot be linked, and other years' evidence is not this year's.
  const { selectedAuditYearId } = useAuditYear();
  const yearId = auditYearId || selectedAuditYearId || undefined;
  const suggestionsQ = useLibrarySuggestions({ sox_control_id: soxControlId, test_run_id: testRunId, audit_year_id: yearId });
  const listQ = useLibraryFiles({ audit_year_id: yearId, q: q || undefined, page, page_size: PAGE_SIZE });

  const sampleIds = useMemo(() => new Set(samples.map((s) => s.id)), [samples]);
  const suggestionById = useMemo(() => {
    const m = new Map<number, FileSuggestion>();
    (suggestionsQ.data?.suggested ?? []).forEach((f) => m.set(f.id, f.suggestion));
    return m;
  }, [suggestionsQ.data]);
  const suggested = suggestionsQ.data?.suggested ?? [];
  const suggestedShown = q
    ? suggested.filter((f) => `${f.relative_path ?? ''} ${f.filename}`.toLowerCase().includes(q.toLowerCase()))
    : suggested;
  // Files already offered in "On this control" are not listed again below.
  const controlUploadIds = useMemo(() => new Set((controlFiles ?? []).map((f) => f.uploadId)), [controlFiles]);
  // Files that match the control or a sample come first, with their reasons, like the suggestions below.
  const controlShown = useMemo(() => (controlFiles ?? [])
    .filter((f) => !q || f.filename.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (suggestionById.get(b.uploadId)?.score ?? -1) - (suggestionById.get(a.uploadId)?.score ?? -1)),
  [controlFiles, q, suggestionById]);
  const notOnControl = (f: EvidenceLibraryFile) => !controlUploadIds.has(f.id);
  const suggestedList = suggestedShown.filter(notOnControl);
  const otherFiles = useMemo(
    () => (listQ.data?.files ?? []).filter((f) => !suggestionById.has(f.id) && !controlUploadIds.has(f.id)),
    [listQ.data, suggestionById, controlUploadIds],
  );
  const pickedCount = chosen.size + chosenControl.size;
  const totalPages = Math.max(1, Math.ceil((listQ.data?.total ?? 0) / PAGE_SIZE));

  const defaultChoice = (file: EvidenceLibraryFile): Choice => {
    const allowed = roles.filter((r) => roleAllowedFor(r, file.filename, populationExts));
    const s = suggestionById.get(file.id);
    const suggestedRole = (s?.suggested_role ?? suggestionsQ.data?.roles?.[String(file.id)]) as EvidenceRole | undefined;
    const role = suggestedRole && allowed.includes(suggestedRole) ? suggestedRole : allowed[0] ?? roles[0];
    return { role, samples: (s?.suggested_samples ?? []).filter((id) => sampleIds.has(id)) };
  };

  const toggle = (file: EvidenceLibraryFile) =>
    setChosen((prev) => {
      const next = new Map(single ? [] : prev);
      if (prev.has(file.id)) next.delete(file.id);
      else next.set(file.id, { file, choice: defaultChoice(file) });
      return next;
    });

  const setChoice = (id: number, choice: Choice) =>
    setChosen((prev) => {
      const next = new Map(prev);
      const cur = next.get(id);
      if (cur) next.set(id, { ...cur, choice });
      return next;
    });

  const disabledReasonFor = (file: EvidenceLibraryFile) =>
    inTestUploadIds?.has(file.id) ? 'In this test'
      : linkedUploadIds?.has(file.id) ? 'Already added to this control' : undefined;
  const selectableFile = (file: EvidenceLibraryFile) =>
    !disabledReasonFor(file) && roles.some((r) => roleAllowedFor(r, file.filename, populationExts));
  const toggleFiles = (files: EvidenceLibraryFile[], select: boolean) =>
    setChosen((prev) => {
      const next = new Map(prev);
      files.forEach((f) => {
        if (select && !next.has(f.id)) next.set(f.id, { file: f, choice: defaultChoice(f) });
        if (!select) next.delete(f.id);
      });
      return next;
    });
  const libraryTree = useMemo(() => buildPathTree(otherFiles, (f) => f.relative_path), [otherFiles]);
  const controlTree = useMemo(() => buildPathTree(controlShown, (f) => f.path), [controlShown]);

  const renderLine = (file: EvidenceLibraryFile, depth = 0, showPath = true) => (
    <FileLine
      key={file.id}
      depth={depth}
      showPath={showPath}
      file={file}
      suggestion={suggestionById.get(file.id)}
      selected={chosen.has(file.id)}
      choice={chosen.get(file.id)?.choice}
      single={single}
      disabledReason={disabledReasonFor(file)}
      roles={roles}
      samples={samples}
      populationExts={populationExts}
      onToggle={() => toggle(file)}
      onChoice={(c) => setChoice(file.id, c)}
    />
  );

  const renderControlLine = (f: ControlFileOption, depth: number) => {
    const on = chosenControl.has(f.evidenceId);
    const id = `ctl-file-${f.evidenceId}`;
    return (
      <li key={f.evidenceId} className={`flex items-start gap-2 px-3 py-2 ${on ? 'bg-blue-50/60' : ''}`} style={depth ? { paddingLeft: 12 + depth * 18 + 20 } : undefined}>
        <input
          id={id}
          type="checkbox"
          className="mt-1"
          checked={on}
          onChange={() => setChosenControl((prev) => {
            const next = new Set(prev);
            if (next.has(f.evidenceId)) next.delete(f.evidenceId);
            else next.add(f.evidenceId);
            return next;
          })}
        />
        <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span className="truncate text-sm font-medium text-gray-800" title={f.filename}>{f.filename}</span>
            <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${ROLE_PILL[f.role]}`}>{ROLE_LABELS[f.role]}</span>
          </div>
          {f.note ? <p className="mt-0.5 text-xs text-amber-700">{f.note}</p> : null}
          {suggestionById.get(f.uploadId)?.reasons?.length ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {suggestionById.get(f.uploadId)!.reasons.map((r) => (
                <span key={r} className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700">{r}</span>
              ))}
            </div>
          ) : null}
        </label>
      </li>
    );
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm(
        Array.from(chosen.values()).map(({ file, choice }) => ({ file, role: choice.role, samples: choice.samples })),
        Array.from(chosenControl).map((evidenceId) => {
          const uploadId = controlFiles?.find((f) => f.evidenceId === evidenceId)?.uploadId;
          const sugg = uploadId != null ? suggestionById.get(uploadId) : undefined;
          return { evidenceId, samples: (sugg?.suggested_samples ?? []).filter((id) => sampleIds.has(id)) };
        }),
      );
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not add the files'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <LibraryDialogShell
      title={title}
      subtitle="Files that match this control are listed first. Nothing changes until you add them."
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          {error ? <span className="mr-auto text-sm text-red-600">{error}</span> : null}
          <button type="button" className={secondaryBtn} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className={primaryBtn} disabled={busy || pickedCount === 0} onClick={confirm}>
            {busy ? <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> : null}
            {confirmLabel}{pickedCount && !single ? ` (${pickedCount})` : ''}
          </button>
        </>
      }
    >
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          data-autofocus
          className={`${fieldClass} pl-9`}
          placeholder="Search by file or folder name"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(1); }}
        />
      </div>

      {controlFiles ? (
        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-700">On this control — not in this test yet</h3>
          {controlShown.length ? (
            <ul className="divide-y divide-gray-100 rounded-lg border border-blue-200">
              <PickerFolderTree
                node={controlTree}
                searching={Boolean(q)}
                renderItem={(f, depth) => renderControlLine(f, depth)}
                isSelectable={() => true}
                isSelected={(f) => chosenControl.has(f.evidenceId)}
                onToggleFolder={(items, select) => setChosenControl((prev) => {
                  const next = new Set(prev);
                  items.forEach((f) => (select ? next.add(f.evidenceId) : next.delete(f.evidenceId)));
                  return next;
                })}
              />
            </ul>
          ) : (
            <p className="py-2 text-sm text-gray-500">{q ? 'No files on this control match your search.' : 'Every evidence file on this control is already in this test.'}</p>
          )}
        </section>
      ) : null}

      <section>
        <h3 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-violet-700">
          <Sparkles className="h-3.5 w-3.5" /> Suggested for this control
        </h3>
        {suggestionsQ.isLoading ? (
          <p className="flex items-center gap-2 py-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Finding matches…</p>
        ) : suggestionsQ.isError ? (
          <p className="py-2 text-sm text-red-600">Could not load suggestions.</p>
        ) : suggestedList.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-violet-200">{suggestedList.map(renderLine)}</ul>
        ) : (
          <p className="py-2 text-sm text-gray-500">No file names or folders match this control{samples.length ? ' or its samples' : ''}.</p>
        )}
        {suggestionsQ.data?.truncated ? <p className="mt-1 text-xs text-amber-600">Only the newest {suggestionsQ.data.total_files} files were checked for matches.</p> : null}
      </section>

      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">{controlFiles ? 'Rest of the library' : 'All files in the library'}</h3>
        {listQ.isLoading ? (
          <p className="flex items-center gap-2 py-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : listQ.isError ? (
          <p className="py-2 text-sm text-red-600">Could not load the library.</p>
        ) : otherFiles.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            <PickerFolderTree
              node={libraryTree}
              searching={Boolean(q)}
              renderItem={(f, depth) => renderLine(f, depth, false)}
              isSelectable={selectableFile}
              isSelected={(f) => chosen.has(f.id)}
              onToggleFolder={single ? undefined : toggleFiles}
            />
          </ul>
        ) : (
          <p className="py-2 text-sm text-gray-500">{q ? 'No other files match your search.' : 'No other files in the library for this year.'}</p>
        )}
        {totalPages > 1 ? (
          <div className="mt-2 flex items-center justify-end gap-2 text-xs text-gray-600">
            <button type="button" className={secondaryBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" className={secondaryBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        ) : null}
      </section>
    </LibraryDialogShell>
  );
}
